import { ImportModule } from "./import-status/import-module";
import {autoinject, Container } from "aurelia-dependency-injection";
import { IDocumentRequestOptions } from "./desktop/interfaces/document-request-options";
import { MyJustisModule } from "./my-justis/my-justis-module";
import { SearchModule } from "./search/search-module";
import { BrowseModule } from "./browse/browse-module";
import { OldBrowseModule } from "./old-browse/browse-module";
import { DesktopModule } from "./desktop/desktop-module";
import { UserHistoryModule } from "./user-history/user-history-module";
import { PrecedentMapModule } from "./precedent-map/precedent-map-module";
import { Utilities } from "./shared/helpers/utilities";
import { AuthModule } from "./authentication/auth-module";
import { MasterView } from "./shared/views/master-view";
import { Navigation } from "./navigation/navigation";
import { IRequestOptions } from "./search/interfaces/request-options";
import { SettingsModule } from "./settings/settings-module";
import { AppEvents } from "./app-events";
import { TemplateManager } from "./shared/helpers/template-manager";
import { routerOptions } from "./router-settings";
import { backboneRouterProperties } from "./decorators/backbone-properties";
import { GoogleAnalytics } from "./shared/helpers/google-analytics";
import { CookieHelper } from "./shared/helpers/cookie-helper";
import "./shared/helpers/google-analytics";
import "./shared/helpers/application-insights";
import { IProductFilter } from "./search/interfaces/product-filter";
import { MobileHelper } from "./shared/helpers/mobile-helper";
import { AuthService } from "./authentication/auth-service";
import { AppUser } from "./authentication/app-user";

declare const System;

@autoinject
// Make router case insensitive
@backboneRouterProperties({
    _routeToRegExp: (route) => {
        route = (Backbone as any).Router.prototype._routeToRegExp.call(this, route);
        return new RegExp(route.source, "i");
    },
})
export class Router extends Backbone.Router {

    private routesHit: number;
    private history: any[];
    private historyClick: boolean;
    private user: AppUser;

    constructor(
        public appEvents: Backbone.Events,
        public myJustisModule: MyJustisModule,
        public searchModule: SearchModule,
        public browseModule: BrowseModule,
        public oldBrowseModule: OldBrowseModule,
        public importModule: ImportModule,
        public desktopModule: DesktopModule,
        public authModule: AuthModule,
        public settingsModule: SettingsModule,
        public userHistoryModule: UserHistoryModule,
        public masterView: MasterView,
        public authService: AuthService,
        public navView: Navigation,
        public precedentMapModule: PrecedentMapModule,
        public google: GoogleAnalytics,
        private templateManager: TemplateManager
     ) {
            super(routerOptions);
            this.history = [];
            this.user = authService.AppUser;
            this.setupEvents();
            this.setupBackForwardButtonInterceptor();
            const mh: MobileHelper = Container.instance.get(MobileHelper);
            if (mh.IsMobileMode) {
                this.navView.setDesktopModel(desktopModule.desktopModel);
            }
    }

    // Anything in this method gets called every time a route is triggered
    public execute(callback, args, name) {
        if (callback) {
            callback.apply(this, args); // Continue along route as planned
        }
    }

    // ROUTE HANDLERS //
    public home(queryString?: string) {
        this.checkForQueryStringCredentials(queryString);
        this.showMyJustis();
        document.title = "JustisOne";
    }

    public async search(query?: string, page?: string, order?: string, exact?: string, availableDate?: string, materialTypes = [], includedCategoryFilters = [], includedDocumentFilters: IProductFilter[] = [], showFullTextOnly = false) {
        if (query) {
            this.showSearch(true);
            if (!order) {
                order = "Relevance";
            }
            if (isNaN(parseInt(page)) || parseInt(page) < 1) { // Most other things are validated in the search model but as this is in the URL its best to do it here
                page = "1";
                let newUrl = `search/${query}/${page}/${order}`;
                if (exact) {
                    newUrl = newUrl + `?exact=${exact}`;
                }
                Backbone.history.navigate(newUrl, {replace: true, trigger: true});
                return;
            }
            await this.searchModule.search(query, parseInt(page, 10), order, availableDate, materialTypes, includedCategoryFilters, includedDocumentFilters, showFullTextOnly);

            _.delay(() => {
                const exactMatchResult = this.searchModule.searchModel.ExactMatchResult;
                // Check query string matches to document (by citation/reference or by name/title). If so, open that document
                if (exact && exact === "1" && exactMatchResult) {
                    this.navigate(`case/${exactMatchResult.ShortTitle}/overview/${exactMatchResult.Id}`, {trigger: true});
                }
            }, 1000);
        } else if (!query) {
            this.showSearch(false);
            this.searchModule.welcome();
        }
        this.google.trackSearch(query);
    }

    public caseDocument(name: string, section: string, id: string, paranum?: number) {
        const documentRequest: IDocumentRequestOptions = {
            id,
            type: "case",
            section: section,
            paragraphNumber: paranum,
            title: name,
            caller: this.getCaller(),
            query: this.searchModule.searchModel.getQueryString(),
            queryComponents: this.searchModule.searchModel.QueryComponents
        };
        this.getDocument(documentRequest);
    }

    public legislationDocument(name: string, statutesection: string, section: string, id: string, reorganise?: boolean) {
        const documentRequest: IDocumentRequestOptions = {
            id,
            type: statutesection ? "statute_section" : "statute",
            section: section,
            statutesection,
            title: name,
            caller: this.getCaller(),
            reorganise,
            query: this.searchModule.searchModel.getQueryString(),
            queryComponents: this.searchModule.searchModel.QueryComponents
        };
        this.getDocument(documentRequest);
    }

    private statutoryInstrumentDocument(name: string, section: string, id: string, reorganise?: boolean) {
        const documentRequest: IDocumentRequestOptions = {
            id,
            type: "si",
            title: name,
            section: section,
            caller: this.getCaller(),
            reorganise,
            query: this.searchModule.searchModel.getQueryString(),
            queryComponents: this.searchModule.searchModel.QueryComponents

        };
        this.getDocument(documentRequest);
    }

    private categoryDocument(name: string, section: string, id: string) {
        const documentRequest: IDocumentRequestOptions = {
            id,
            type: "category",
            section: section,
            title: name,
            caller: this.getCaller(),
            query: this.searchModule.searchModel.getQueryString(),
        };
        this.getDocument(documentRequest);
    }

    private EUDocument(sector: string, name: string, section: string, id: string, paranum?: number) {
        const realType = this.getEUDocumentType(sector);
        const documentRequest: IDocumentRequestOptions = {
            id,
            type: realType,
            title: name,
            section: section,
            paragraphNumber: paranum,
            caller: this.getCaller(),
            query: this.searchModule.searchModel.getQueryString(),
            queryComponents: this.searchModule.searchModel.QueryComponents

        };
        this.getDocument(documentRequest);
    }

    private genericDocument(name: string, section: string, id: string, paranum?: number) {
        const documentRequest: IDocumentRequestOptions = {
            id,
            title: name,
            section: section,
            type: "document",
            paragraphNumber: paranum,
            caller: this.getCaller(),
            query: this.searchModule.searchModel.getQueryString(),
            queryComponents: this.searchModule.searchModel.QueryComponents

        };
        this.getDocument(documentRequest);
    }

    private precedentMap(name: string, id: string, unique: string) {
        this.showDesktop();
        const desktopTransition = this.desktopModule.prepareDesktopForPrecedentMap(id);
        const prepare = this.precedentMapModule.setupMap();
        Promise.all([desktopTransition, prepare]).then((model: any[]) => {
            this.precedentMapModule.setExitPoint(name, id);
            this.precedentMapModule.enterPrecedentMap(id, unique, this.historyClick);
            this.historyClick = false;
        });
        this.navView.hide();
        this.google.trackPrecedentMap(name);
    }

    private auth() {
        if (this.user.IsAuthenticated) {
            this.showSearch(false);
        } else {
            this.doSignIn();
        }
    }

    private importStatus(dataSource: string) {
        this.hideAllPanels();
        this.importModule.show(dataSource);
        this.navView.select("import-status");
    }

    private signIn(queryString: string): void {
        if (this.user.IsAuthenticated) {
            Backbone.history.navigate("my-justis", {trigger: true});
            return;
        }
        this.doSignIn(queryString);
    }

    private doSignIn(queryString?: string) {
        document.title = "Sign In - JustisOne";
        let params: any = {};
        if (queryString) {
            params = Utilities.parseQueryString(queryString);
        }
        this.desktopModule.removeAllDocumentHolders();
        this.settingsModule.reset();
        this.authModule.showSignIn({ cancelUrl: params.return, successUrl: params.return, email: params.email });
    }

    private override(returnUrl?: string) {
        document.title = "Sign In - JustisOne";
        if (returnUrl) {
            returnUrl = returnUrl.replace("return=", "");
        }
        this.desktopModule.removeAllDocumentHolders();
        this.searchModule.reset();
        this.settingsModule.reset();
        this.authModule.showSignIn({ cancelUrl: "my-justis", successUrl: returnUrl, override: true });
    }

    private showUserHistory(section?: string, page?: number): void {
        document.title = "History - JustisOne";
        this.hideAllPanels();
        this.navigate("./my-justis/history", { trigger: false });
        this.showMyJustis("history");
    }

    private invokeProcessingPackage() {
        document.title = "Processing - JustisOne";
        System.import("./processing/index").then((proc) => {
            const init = new proc.Initializer();
        }, "processing");
    }

    private fullscreenReader(content: any) {
        this.desktopModule.showFullscreenReader(content);
    }

    private wayfless(queryString: string) {
        let params: any = {};
        if (queryString) {
            params = Utilities.parseQueryString(queryString);
            if (params.entityId) {
                this.authModule.wayflessSignIn(params.entityId, params.returnUrl);
            }
        }
    }

    private activeDirectoryDirect(queryString: string) {
        queryString = queryString || "";
        const params = Utilities.parseQueryString(queryString);
        this.authModule.activeDirectoryDirectSignIn(params.returnUrl);
    }

    // GLOBAL EVENTS //

    private setupEvents(): void {
        this.appEvents.on(AppEvents.SignOut, async () => { await this.signOut(); });
        this.appEvents.on(AppEvents.HistoryBack, () => { this.back(); });
        this.appEvents.on(AppEvents.CloseAdvanced, () => { this.hideAdvancedSearch(); });
        this.appEvents.on(AppEvents.ClearSearch, () => { this.clearSearch(); });
        this.appEvents.on(AppEvents.SettingsVisibilityToggled, () => { this.settings(); });
        this.appEvents.on(AppEvents.SettingsClose, () => { this.settingsClose(); });
        this.appEvents.on(AppEvents.DocumentRequested, (options: IDocumentRequestOptions) => this.handleDocumentRequestEvent(options));
        this.appEvents.on("FullScreen", (content) => this.handleFullScreenReaderEvent(content));
        this.appEvents.on(AppEvents.SearchRequest, async (options: IRequestOptions) => await this.handleSearchRequestEvent(options));
        this.appEvents.on(AppEvents.BrowseSearchRequest, async (options: IRequestOptions) => await this.handleBrowseRequestEvent(options));
        this.appEvents.on(AppEvents.SigninSuccessful, (returnUrl: string) => this.handleSignInSuccessfulEvent(returnUrl));
        this.appEvents.on(AppEvents.ViewStatuteOutline, (url: string) => { this.navigate(url, { trigger: true }); });
        this.listenTo(this, "route", (name, args) => this.handleAllRouteChanges(name, args));
        this.listenTo(this.user, "change:IsAuthenticated", this.authenticatedChanged);
        this.listenTo(this.user, "refresh", this.authenticatedChanged);

        const mh: MobileHelper = Container.instance.get(MobileHelper);
        if (mh.IsMobileMode) {
            this.appEvents.on(AppEvents.MobileDocumentListToggled, () => { this.mobileDocumentListToggle(); });
        }

        Backbone.history.on("all", (route, router) => {
            this.desktopModule.hideFullscreenReader();
        });
    }

    private handleSignInSuccessfulEvent(returnUrl: string) {
        if (returnUrl) {
            returnUrl = returnUrl.replace((Backbone.history as any).root, "");
            if (returnUrl.toLowerCase().indexOf("register/upgrade") !== -1) {
                window.location.href = (Backbone.history as any).root + returnUrl;
            } else {
                this.navigate(returnUrl, { trigger: true });
            }

        } else {
            this.navigate("search", { trigger: true });
        }
    }

    private clearAllPopovers() {
        // On route change we want to close any open dropdowns, except those specifically set with data-keep-dropdown-open='true'
        // This is in place so the open documents list can stay open even URL changes due to closing the current document;
        $(".popover").popover("hide");
        $("[data-toggle='dropdown']").not("[data-keep-dropdown-open='true']").parent().removeClass("open");
        $("[data-keep-dropdown-open='true']").removeAttr("data-keep-dropdown-open"); // unset this attribute to resume normal behaviour
    }

    private storeRouteHistory(name, args) {
        this.history.push({
            name,
            args,
            fragment: Backbone.history.fragment
        });
    }

    private handleAllRouteChanges(name, args) {
        this.clearAllPopovers();
        this.storeRouteHistory(name, args);
    }

    private handleFullScreenReaderEvent(content) {
        this.fullscreenReader(content);
    }

    private handleDocumentRequestEvent(options: IDocumentRequestOptions) {
        options.caller = this.getCaller();
        this.getDocument(options);
    }

    private async handleSearchRequestEvent(options: IRequestOptions) {
        const urlFriendlyQuery = options.query.replace("*", "{asterisk}");
        const availableDateUrlPart = options.availableDate ? "/?AvailableDate=" + options.availableDate : "";
        const url = "search/" + encodeURIComponent(urlFriendlyQuery) + "/" + options.page + "/" + options.order + availableDateUrlPart;
        this.navigate(url, { trigger: false });
        await this.search(options.query, options.page, options.order, "", options.availableDate, options.materialTypes, options.includedCategoryFilters, options.includedProductFilters, options.showFullTextOnly);
    }

    private async handleBrowseRequestEvent(options: IRequestOptions) {
        const urlFriendlyQuery = options.query.replace("*", "{asterisk}");
        const url = `browsesearch/${encodeURIComponent(urlFriendlyQuery)}/1/Relevance`;
        this.navigate(url, { trigger: false });
        await this.search(options.query, "1", "Relevance", "", null, [], [], [], options.showFullTextOnly);
    }

    private mobileDocumentListToggle() {
        const documentHolders = this.desktopModule.desktopModel.DocumentHolders;
        this.navView.toggleMobileDocumentList(documentHolders);
        Backbone.history.navigate("/desktop", { trigger: true});
    }

    private authenticatedChanged() {
        this.navView.initialiseBrowseButton();
        this.templateManager.reset();
    }

    private shibbolethLoginViaWayfless() {
        const fragment =  JSON.parse(CookieHelper.getCookie("ShibbolethAccessToken"));
        // this.user.externalAuthCallback(fragment);
    }

    // HELPER FUNCTIONS //
    private setupBackForwardButtonInterceptor() {
        window.onpopstate = () => {
            this.historyClick = true;
        };
    }

    private getDocument(documentRequest: IDocumentRequestOptions) {
        this.showDesktop();
        const from = this.history[this.history.length - 1] ? this.history[this.history.length - 1].name : "";
        if (from === "precedentMap") {
            this.documentFromPrecedentMap(documentRequest);
        } else {
            this.desktopModule.requestDocument(documentRequest);
        }
        this.google.trackDocument(documentRequest.title);
    }

    private documentFromPrecedentMap(documentRequest: IDocumentRequestOptions) {
        documentRequest.caller = "precedent-map";
        this.showDesktop();
        this.desktopModule.enterDesktopFromPrecedentMap(documentRequest);
        this.precedentMapModule.exitPrecedentMapToDocument();
        this.navView.unhide();
    }

    private browse(levels?: string) {
        this.hideAllPanels();
        // This will stop renedering the browse panels if a user tries to enable the button from the fron-end when they are not a pro user
        if (this.user.Sku !== "pro") {
            return;
        }

        this.browseModule.show(levels);
        this.navView.select("browse");
        document.title = "Browse - JustisOne";
    }

    private showMyJustis(section?: string, subsection?: string) {
        this.hideAllPanels();
        this.navView.select("my-justis");
        this.myJustisModule.show(section, subsection);
        document.title = "JustisOne";
    }

    private showSearch(executingSearch) {
        this.hideAllPanels();
        this.navView.select("search");
        if (executingSearch || this.searchModule.searchModel.Results.size()) {
            this.searchModule.show();
        } else {
            this.myJustisModule.show();
        }
        document.title = "Search - JustisOne";
    }

    private showDesktop() {
        this.hideAllPanels();
        this.desktopModule.show();
        this.navView.select("desktop");
    }

    private showAdvancedSearch(formID?: string) {
        this.hideAllPanels();
        this.searchModule.show();
        this.searchModule.showAdvancedSearch(formID);
        this.navView.select("advanced-search");
        document.title = "Advanced Search - JustisOne";
    }

    private checkForQueryStringCredentials(queryString?: string) {
        let params: any = {};
        if (queryString) {
            params = Utilities.parseQueryString(queryString);
            if (params.username && params.password) {
                this.authModule.urlSignin(params.username, params.password).then(() => {
                    this.navigate("", { trigger: true })
                    this.masterView.hideThinker();
                }).catch((err) => {
                    this.appEvents.trigger(AppEvents.ErrorMessage, err.message);
                    this.masterView.hideThinker();
                    this.navigate("", { trigger: true })
                });
            }
        }
    }

    private hideAllPanels() {
        this.myJustisModule.hide();
        this.desktopModule.hide();
        this.searchModule.hide();
        this.browseModule.hide();
        this.oldBrowseModule.hide();
        this.settingsModule.hide();
        this.userHistoryModule.hide();
        this.importModule.hide();
        this.masterView.clearDialogs();
        this.clearPackages();
    }

    private hideAdvancedSearch() {
        this.searchModule.closeAdvanced();
    }

    private clearPackages() {
        $(".package").addClass("vanish");
    }

    private settings() {
        const mh: MobileHelper = Container.instance.get(MobileHelper);
        this.settingsModule.openOrClose();
        if (mh.IsMobileMode) {
            this.navView.select("settings");
        } else {

            this.navView.toggleSettings();
        }
        this.appEvents.trigger(AppEvents.AppInsightsSettingsVisibilityToggled, {Mode: $("#settings-region").attr("class")});
    }

    private settingsClose() {
        this.settingsModule.hide();
    }

    private async signOut() {
        await this.authModule.signOut();
        this.desktopModule.removeAllDocumentHolders();
        this.appEvents.trigger(AppEvents.DesktopDocumentRemoved, { documentCount: 0 });
        this.searchModule.reset();
        this.settingsModule.reset();
        this.userHistoryModule.reset();
        this.precedentMapModule.hide();
        this.templateManager.reset([]);
        this.browseModule.reset();
        this.oldBrowseModule.reset();
        this.navigate("search", { trigger: true });
    }

    private back(): void {
        if (this.routesHit > 1) {
            this.routesHit = this.routesHit - 2;
            window.history.back();
        } else {
            if (Backbone.history.getFragment() !== "my-justis") {
                this.routesHit = 0;
            }
            this.navigate("search", { trigger: true, replace: true });
        }
    }

    private getCaller(): any {
        if (this.searchModule.active) {
            return this.searchModule.search;
        } else {
            return this.desktopModule.desktopModel.Focus;
        }
    }

    private clearSearch() {
        this.searchModule.reset();
        this.navigate("search", { trigger: true });
        this.search();
    }

    private error(httpCode: string): void {
        const status: string = httpCode.replace(/\D+/g, ""); // Keep only numbers just incase they have crept in somehow
        switch (status) {
            case "404":
                this.appEvents.trigger(AppEvents.ErrorMessage, "Oops...we couldn't find that page!. Please try again");
                break;
            default:
                this.appEvents.trigger(AppEvents.ErrorMessage, "Oops...we couldn't find that!. Please try again");
                break;
        }
    }

    private getEUDocumentType(sector: string): JustisOneDocumentType {
        switch (sector) {
            case "0":
                return "eu_sector0";
            case "1":
                return "eu_sector1";
            case "2":
                return "eu_sector2";
            case "3":
                return "eu_sector3";
            case "4":
                return "eu_sector4";
            case "5":
                return "eu_sector5";
            case "6":
                return "eu_sector6";
            case "7":
                return "eu_sector7";
            case "8":
                return "eu_sector8";
            case "9":
                return "eu_sector9";
            case "c":
                return "eu_sectorc";
            default:
                return "document";

        }
    }
    private alertsSearchEdit(id: string) {
        alert(`in alertsSearchEdit(): ${id}`);
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/router.ts